import React, { useState, useEffect } from 'react';
import { getTicketDetail, addResponse, updateTicketStatus } from '../../services/supportApi';
import configData from '../../config.json';
import './TicketDetail.css';

function TicketDetail({ ticketId, onClose, isStaff = false }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newResponse, setNewResponse] = useState('');
  const [responseFiles, setResponseFiles] = useState([]);
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [responseError, setResponseError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetail();
    }
  }, [ticketId]);

  const fetchTicketDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getTicketDetail(ticketId);
      setTicket(response.data);
    } catch (err) {
      console.error('Failed to fetch ticket detail:', err);
      setError('Failed to load ticket details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open':
        return 'status-open';
      case 'in_progress':
        return 'status-in-progress';
      case 'completed':
        return 'status-completed';
      case 'closed':
        return 'status-closed';
      default:
        return '';
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!newResponse.trim()) return;

    setSubmittingResponse(true);
    setResponseError(null);

    try {
      await addResponse(ticketId, newResponse.trim(), responseFiles);
      setNewResponse('');
      setResponseFiles([]);
      // Refresh ticket to show new response
      await fetchTicketDetail();
    } catch (err) {
      console.error('Failed to submit response:', err);
      setResponseError('Failed to submit response. Please try again.');
    } finally {
      setSubmittingResponse(false);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    if (newStatus === ticket.status) return;

    setUpdatingStatus(true);
    try {
      await updateTicketStatus(ticketId, newStatus);
      await fetchTicketDetail();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'closed', label: 'Closed' }
  ];

  // Group attachments by field_name for inline display
  const getAttachmentsByField = () => {
    if (!ticket?.attachments) return {};
    const grouped = {};
    ticket.attachments.forEach((attachment) => {
      const fieldName = attachment.field_name || '_unassociated';
      if (!grouped[fieldName]) {
        grouped[fieldName] = {
          label: attachment.field_label,
          files: []
        };
      }
      grouped[fieldName].files.push(attachment);
    });
    return grouped;
  };

  const renderFieldValue = (fieldValue, attachmentsByField) => {
    const { value, field_type, field_label, field_name } = fieldValue;

    let displayValue = value;

    if (Array.isArray(value)) {
      displayValue = value.join(', ');
    } else if (typeof value === 'boolean') {
      displayValue = value ? 'Yes' : 'No';
    } else if (value === null || value === undefined || value === '') {
      displayValue = '—';
    }

    return (
      <div key={fieldValue.id} className="ticketDetail-field">
        <div className="ticketDetail-field-label">{field_label}</div>
        <div className="ticketDetail-field-value">{displayValue}</div>
      </div>
    );
  };

  const renderFileField = (fieldName, fieldData) => {
    return (
      <div key={fieldName} className="ticketDetail-field">
        <div className="ticketDetail-field-label">{fieldData.label}</div>
        <div className="ticketDetail-field-value ticketDetail-field-attachments">
          {fieldData.files.map((attachment) => (
            <a
              key={attachment.id}
              href={`${configData.API_URL}${attachment.file}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ticketDetail-attachment"
            >
              {attachment.file_alt || attachment.file.split('/').pop()}
            </a>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="ticketDetail">
        <div className="ticketDetail-header">
          <button onClick={onClose} className="ticketDetail-back">Back to Tickets</button>
        </div>
        <div className="ticketDetail-loading">Loading ticket details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ticketDetail">
        <div className="ticketDetail-header">
          <button onClick={onClose} className="ticketDetail-back">Back to Tickets</button>
        </div>
        <div className="ticketDetail-error">
          {error}
          <button onClick={fetchTicketDetail} className="ticketDetail-retry">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return null;
  }

  return (
    <div className="ticketDetail">
      <div className="ticketDetail-header">
        <button onClick={onClose} className="ticketDetail-back">Back to Tickets</button>
      </div>

      <div className="ticketDetail-title">
        <div className="ticketDetail-title-row">
          <span className="ticketDetail-id">Ticket {ticket.client_code ? `${ticket.client_code}-${ticket.id}` : `#${ticket.id}`}</span>
          {isStaff ? (
            <select
              className={`ticketDetail-status-select ${getStatusClass(ticket.status)}`}
              value={ticket.status}
              onChange={handleStatusChange}
              disabled={updatingStatus}
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : (
            <span className={`ticketDetail-status ${getStatusClass(ticket.status)}`}>
              {ticket.status_display}
            </span>
          )}
        </div>
        <h2 className="ticketDetail-subject">{ticket.subject}</h2>
      </div>

      <div className="ticketDetail-meta">
        <div className="ticketDetail-meta-item">
          <span className="ticketDetail-meta-label">Type:</span>
          <span className="ticketDetail-meta-value">{ticket.form_type_name}</span>
        </div>
        <div className="ticketDetail-meta-item">
          <span className="ticketDetail-meta-label">Submitted:</span>
          <span className="ticketDetail-meta-value">{formatDate(ticket.created_at)}</span>
        </div>
        <div className="ticketDetail-meta-item">
          <span className="ticketDetail-meta-label">By:</span>
          <span className="ticketDetail-meta-value">{ticket.user_name} ({ticket.user_email})</span>
        </div>
        {ticket.cc_users && ticket.cc_users.length > 0 && (
          <div className="ticketDetail-meta-item">
            <span className="ticketDetail-meta-label">CC:</span>
            <span className="ticketDetail-meta-value">
              {ticket.cc_users.map((cu, i) => (
                <span key={cu.id}>
                  {i > 0 && ', '}
                  {cu.name}{cu.email ? ` (${cu.email})` : ''}
                </span>
              ))}
            </span>
          </div>
        )}
        {ticket.client_name && (
          <div className="ticketDetail-meta-item">
            <span className="ticketDetail-meta-label">Client:</span>
            <span className="ticketDetail-meta-value">{ticket.client_name}</span>
          </div>
        )}
        {ticket.product_name && (
          <div className="ticketDetail-meta-item">
            <span className="ticketDetail-meta-label">Product:</span>
            <span className="ticketDetail-meta-value">{ticket.product_name}</span>
          </div>
        )}
        <div className="ticketDetail-meta-item">
          <span className="ticketDetail-meta-label">Last Updated:</span>
          <span className="ticketDetail-meta-value">{formatDate(ticket.updated_at)}</span>
        </div>
      </div>

      {ticket.status_logs && ticket.status_logs.length > 0 && (
        <div className="ticketDetail-section">
          <h3>Status History</h3>
          <div className="ticketDetail-status-logs">
            {ticket.status_logs.map((log) => (
              <div key={log.id} className="ticketDetail-status-log">
                <span className="ticketDetail-status-log-change">
                  <span className={`ticketDetail-status-badge ${getStatusClass(log.old_status)}`}>
                    {log.old_status_display}
                  </span>
                  <span className="ticketDetail-status-arrow">→</span>
                  <span className={`ticketDetail-status-badge ${getStatusClass(log.new_status)}`}>
                    {log.new_status_display}
                  </span>
                </span>
                <span className="ticketDetail-status-log-meta">
                  by {log.user_name || 'System'} on {formatDate(log.changed_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(() => {
        const attachmentsByField = getAttachmentsByField();
        const hasFieldValues = ticket.field_values && ticket.field_values.length > 0;
        const hasFileFields = Object.keys(attachmentsByField).filter(k => k !== '_unassociated').length > 0;
        const unassociatedAttachments = attachmentsByField['_unassociated']?.files || [];

        return (
          <>
            {(hasFieldValues || hasFileFields) && (
              <div className="ticketDetail-section">
                <h3>Submission Details</h3>
                <div className="ticketDetail-fields">
                  {ticket.field_values && ticket.field_values.map((fv) => renderFieldValue(fv, attachmentsByField))}
                  {Object.entries(attachmentsByField)
                    .filter(([fieldName]) => fieldName !== '_unassociated')
                    .map(([fieldName, fieldData]) => renderFileField(fieldName, fieldData))}
                </div>
              </div>
            )}

            {unassociatedAttachments.length > 0 && (
              <div className="ticketDetail-section">
                <h3>Attachments</h3>
                <div className="ticketDetail-attachments">
                  {unassociatedAttachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={`${configData.API_URL}${attachment.file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ticketDetail-attachment"
                    >
                      {attachment.file_alt || attachment.file.split('/').pop()}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      <div className="ticketDetail-section">
        <h3>Conversation</h3>
        {ticket.responses && ticket.responses.length > 0 ? (
          <div className="ticketDetail-responses">
            {ticket.responses.map((response) => (
              <div key={response.id} className={`ticketDetail-response ${response.is_staff ? 'is-staff' : 'is-customer'}`}>
                <div className="ticketDetail-response-header">
                  <span className="ticketDetail-response-author">
                    {response.user_name}
                    {response.is_staff && <span className="ticketDetail-staff-badge">Staff</span>}
                  </span>
                  <span className="ticketDetail-response-date">{formatDate(response.created_at)}</span>
                </div>
                <div className="ticketDetail-response-message">{response.message}</div>
                {response.attachments && response.attachments.length > 0 && (
                  <div className="ticketDetail-response-attachments">
                    {response.attachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={`${configData.API_URL}${attachment.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ticketDetail-response-attachment"
                      >
                        {attachment.file_alt || attachment.file.split('/').pop()}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="ticketDetail-no-responses">No responses yet.</p>
        )}

        {/* Response form */}
        <form className="ticketDetail-response-form" onSubmit={handleSubmitResponse}>
          <textarea
            value={newResponse}
            onChange={(e) => setNewResponse(e.target.value)}
            placeholder="Add a response..."
            rows={4}
            disabled={submittingResponse}
          />
          <div className="ticketDetail-response-file-input">
            <label>
              Attach files (optional)
              <input
                type="file"
                multiple
                onChange={(e) => setResponseFiles(Array.from(e.target.files))}
                disabled={submittingResponse}
              />
            </label>
            {responseFiles.length > 0 && (
              <div className="ticketDetail-response-file-list">
                {responseFiles.map((file, index) => (
                  <span key={index} className="ticketDetail-response-file-name">{file.name}</span>
                ))}
              </div>
            )}
          </div>
          {responseError && <div className="ticketDetail-response-error">{responseError}</div>}
          <button
            type="submit"
            disabled={submittingResponse || !newResponse.trim()}
          >
            {submittingResponse ? 'Sending...' : 'Send Response'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default TicketDetail;
